// SPDX-License-Identifier: AGPL-3.0-or-later

import {DESKTOP_APP_PROTOCOL_PREFIX} from '@app/features/ui/utils/AppProtocol';
import {
	CONNECTION_AUTHORIZATION_STATE_EXPIRY_MS,
	ConnectionTypes,
	isOAuthConnectionType,
	type OAuthConnectionType,
} from '@fluxer/constants/src/ConnectionConstants';
import type {CompleteConnectionRequest} from '@fluxer/schema/src/domains/connection/ConnectionSchemas';

export type CompletableConnectionType = OAuthConnectionType | typeof ConnectionTypes.BLUESKY;

export type ConnectionCallbackResult =
	| {kind: 'complete'; request: CompleteConnectionRequest}
	| {kind: 'error'; reason: string}
	| {kind: 'invalid'};

const STATE_MAX_LENGTH = 128;
const CODE_MAX_LENGTH = 2048;
const OPENID_KEY = /^openid\.[a-z_.]{1,48}$/;
const OPENID_VALUE_MAX_LENGTH = 2048;
const DESKTOP_HANDOFF_PREFIX = `${DESKTOP_APP_PROTOCOL_PREFIX}connection-callback/`;
const SIGN_IN_MARKER_KEY = 'micup.connection_sign_in';
const CANCELLED = 'access_denied';
const INVALID: ConnectionCallbackResult = {kind: 'invalid'};

export function isCompletableConnectionType(type: string): type is CompletableConnectionType {
	return type === ConnectionTypes.BLUESKY || isOAuthConnectionType(type);
}

function boundedParam(params: URLSearchParams, key: string, maxLength: number): string | null {
	const value = params.get(key);
	return value && value.length <= maxLength ? value : null;
}

export function parseConnectionCallback(type: string, search: string): ConnectionCallbackResult {
	if (!isCompletableConnectionType(type)) return INVALID;
	const params = new URLSearchParams(search);
	const providerError =
		params.get('error') ?? (params.get('status') === 'error' ? (params.get('reason') ?? 'unknown') : null);
	if (providerError !== null) return {kind: 'error', reason: providerError};
	const openidMode = params.get('openid.mode');
	if (type === ConnectionTypes.STEAM && openidMode !== null && openidMode !== 'id_res') {
		return {kind: 'error', reason: CANCELLED};
	}
	const state = boundedParam(params, 'state', STATE_MAX_LENGTH);
	if (!state) return INVALID;
	if (type === ConnectionTypes.BLUESKY) return {kind: 'complete', request: {state}};
	if (type === ConnectionTypes.STEAM) {
		const openid: Record<string, string> = {};
		for (const [key, value] of params) {
			if (!OPENID_KEY.test(key)) continue;
			if (value.length > OPENID_VALUE_MAX_LENGTH) return INVALID;
			openid[key] = value;
		}
		return openidMode ? {kind: 'complete', request: {state, openid}} : INVALID;
	}
	const code = boundedParam(params, 'code', CODE_MAX_LENGTH);
	return code ? {kind: 'complete', request: {state, code}} : INVALID;
}

export function buildDesktopHandoffUrl(type: CompletableConnectionType, request: CompleteConnectionRequest): string {
	const params = new URLSearchParams({state: request.state});
	if (request.code) params.set('code', request.code);
	for (const [key, value] of Object.entries(request.openid ?? {})) params.set(key, value);
	return `${DESKTOP_HANDOFF_PREFIX}${type}?${params.toString()}`;
}

interface SignInMarker {
	type: string;
	state: string | null;
	at: number;
}

const MAX_SIGN_IN_MARKERS = 8;

function readSignInMarkers(storage: Storage | null, now: number): Array<SignInMarker> {
	try {
		const markers: unknown = JSON.parse(storage?.getItem(SIGN_IN_MARKER_KEY) ?? '[]');
		if (!Array.isArray(markers)) return [];
		return markers.filter(
			(marker): marker is SignInMarker =>
				typeof marker?.type === 'string' &&
				(marker.state === null || typeof marker.state === 'string') &&
				typeof marker.at === 'number' &&
				now - marker.at <= CONNECTION_AUTHORIZATION_STATE_EXPIRY_MS,
		);
	} catch {
		return [];
	}
}

function writeSignInMarkers(storage: Storage | null, markers: Array<SignInMarker>): void {
	try {
		storage?.setItem(SIGN_IN_MARKER_KEY, JSON.stringify(markers.slice(-MAX_SIGN_IN_MARKERS)));
	} catch {}
}

function authorizeUrlState(authorizeUrl: string): string | null {
	try {
		const params = new URL(authorizeUrl).searchParams;
		const returnTo = params.get('openid.return_to');
		return params.get('state') ?? (returnTo ? new URL(returnTo).searchParams.get('state') : null);
	} catch {
		return null;
	}
}

function matchesSignIn(marker: SignInMarker, type: string, state: string): boolean {
	return marker.type === type && (marker.state === null || marker.state === state);
}

export function rememberConnectionSignIn(
	storage: Storage | null,
	type: CompletableConnectionType,
	authorizeUrl: string,
	now: number,
): void {
	const state = type === ConnectionTypes.BLUESKY ? null : authorizeUrlState(authorizeUrl);
	writeSignInMarkers(storage, [...readSignInMarkers(storage, now), {type, state, at: now}]);
}

export function isConnectionSignInPending(storage: Storage | null, type: string, state: string, now: number): boolean {
	return readSignInMarkers(storage, now).some((marker) => matchesSignIn(marker, type, state));
}

export function forgetConnectionSignIn(storage: Storage | null, type: string, state: string, now: number): void {
	writeSignInMarkers(
		storage,
		readSignInMarkers(storage, now).filter((marker) => !matchesSignIn(marker, type, state)),
	);
}
